import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, Eye, Download, FileSpreadsheet, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { downloadInvoice } from '../../lib/invoiceUtils';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { PLACEHOLDER_IMAGE } from '../../lib/placeholder';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;
const PAGE_SIZE = 15;

export default function AdminOrders() {
  const [allOrders, setAllOrders] = useState([]);   // full list from API
  const [orders, setOrders] = useState([]);          // current page slice
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  // Fetch ALL orders once
  const fetchOrders = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/orders`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : (data.orders || []);
        setAllOrders(list);
      }
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Client-side filter + paginate
  useEffect(() => {
    let filtered = allOrders;

    if (statusFilter !== 'all') {
      filtered = filtered.filter(o => o.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(o =>
        o.order_id?.toLowerCase().includes(q) ||
        o.customer?.name?.toLowerCase().includes(q) ||
        o.customer?.email?.toLowerCase().includes(q)
      );
    }

    const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, pages);
    setTotalPages(pages);
    setOrders(filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE));
  }, [allOrders, statusFilter, searchQuery, page]);

  // Reset to page 1 when filter/search changes
  useEffect(() => { setPage(1); }, [statusFilter, searchQuery]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/orders/${orderId}/status?status=${newStatus}`, {
        method: 'PUT',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to update status');

      toast.success('Order status updated');
      await fetchOrders();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getImageUrl = (url) => {
    if (!url) return PLACEHOLDER_IMAGE;
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'bg-success/10 text-success';
      case 'shipped': return 'bg-primary/10 text-primary';
      case 'confirmed': return 'bg-warning/10 text-warning';
      case 'cancelled': return 'bg-destructive/10 text-destructive';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const exportToExcel = async () => {
    try {
      setIsExporting(true);
      const token = localStorage.getItem('token');
      
      // We can pass current filters to the export endpoint if supported
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (searchQuery.trim()) params.append('search', searchQuery);
      
      const queryString = params.toString() ? `?${params.toString()}` : '';
      
      const response = await fetch(`${API}/admin/orders/export${queryString}`, {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to export orders');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Orders_Export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Orders exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export orders');
    } finally {
      setIsExporting(false);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, page - delta);
    const right = Math.min(totalPages - 1, page + delta);

    pages.push(1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 h-16 animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-orders">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <h1 className="font-heading text-2xl md:text-3xl text-secondary">Orders</h1>
        <div className="flex gap-4">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by Order ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 rounded-xl">
              <SelectValue placeholder="Filter status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Orders</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <button
            onClick={exportToExcel}
            disabled={isExporting}
            className="flex items-center gap-2 px-4 py-2 bg-success text-white rounded-xl hover:bg-success/90 transition-all font-body font-medium shadow-sm active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="export-excel-btn"
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="hidden sm:inline">Exporting...</span>
              </>
            ) : (
              <>
                <FileSpreadsheet className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Summary */}
      <p className="text-sm text-muted-foreground font-body">
        Showing {orders.length} of {allOrders.length} orders
      </p>

      {/* Orders Table */}
      <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-surface/50">
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Order ID</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Customer</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Items</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Total</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Payment</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Status</th>
                <th className="text-left py-4 px-4 font-body font-medium text-muted-foreground text-sm">Date</th>
                <th className="text-right py-4 px-4 font-body font-medium text-muted-foreground text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.length > 0 ? orders.map((order) => (
                <tr key={order.order_id} className="border-b border-gray-50 hover:bg-surface/30">
                  <td className="py-4 px-4">
                    <Link to={`/admin/orders/${order.order_id}`} className="font-body font-medium text-primary hover:underline">
                      #{order.order_id}
                    </Link>
                  </td>
                  <td className="py-4 px-4">
                    <p className="font-body text-sm text-secondary">{order.customer?.name || order.user_name || 'N/A'}</p>
                    <p className="font-body text-xs text-muted-foreground">{order.customer?.email || order.user_email || 'N/A'}</p>
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex -space-x-2">
                      {order.items?.slice(0, 3).map((item, idx) => (
                        <img
                          key={idx}
                          src={getImageUrl(item.image)}
                          alt={item.name}
                          className="w-8 h-8 rounded-lg object-cover border-2 border-white"
                          title={item.name}
                        />
                      ))}
                      {order.items?.length > 3 && (
                        <div className="w-8 h-8 rounded-lg bg-surface flex items-center justify-center border-2 border-white">
                          <span className="text-xs text-muted-foreground">+{order.items.length - 3}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="py-4 px-4 font-body font-medium text-secondary">
                    {formatPrice(order.total || order.total_amount)}
                  </td>
                  <td className="py-4 px-4">
                    <Badge className={order.payment_status === 'paid' ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'}>
                      {order.payment_status || 'N/A'}
                    </Badge>
                  </td>
                  <td className="py-4 px-4">
                    <Select
                      value={order.status}
                      onValueChange={(value) => updateOrderStatus(order.order_id, value)}
                    >
                      <SelectTrigger className={`w-32 h-8 text-xs rounded-full ${getStatusColor(order.status)}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="confirmed">Confirmed</SelectItem>
                        <SelectItem value="shipped">Shipped</SelectItem>
                        <SelectItem value="delivered">Delivered</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-4 px-4 font-body text-sm text-muted-foreground">
                    {formatDate(order.created_at)}
                  </td>
                  <td className="py-4 px-4">
                    <div className="flex justify-end gap-2">
                      <Link
                        to={`/admin/orders/${order.order_id}`}
                        className="p-2 hover:bg-surface rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      </Link>
                      <button
                        onClick={() => downloadInvoice(order)}
                        className="p-2 hover:bg-surface rounded-lg transition-colors"
                        title="Download Invoice"
                      >
                        <Download className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-muted-foreground font-body">
                    No orders found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
            <span className="text-sm text-muted-foreground font-body">
              Page {page} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              {/* Prev */}
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg font-body text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-surface text-secondary hover:bg-gray-200"
              >
                ← Prev
              </button>

              {/* Page numbers */}
              {getPageNumbers().map((p, idx) =>
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground font-body text-sm">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`w-8 h-8 rounded-full font-body text-sm transition-all ${page === p
                      ? 'bg-primary text-white shadow-sm'
                      : 'bg-surface text-secondary hover:bg-gray-200'
                      }`}
                  >
                    {p}
                  </button>
                )
              )}

              {/* Next */}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 rounded-lg font-body text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-surface text-secondary hover:bg-gray-200"
              >
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
